import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { expand, map, Observable, of, reduce } from "rxjs";

import { AsyncStorage } from "@core/services/AsyncStorage";
import { User } from "@core/data/user";
import { UserReport, UserReportData } from "@core/data/userReport";
import { APIResponse } from '@shared/types.barrel';
import { ClientsService, Client } from '@admin/clients/clients.service';
import { LoyaltyProgram } from '@financial/loyalty/loyalty.service';
import { UserService } from '@admin/users/user.service';
import { DebugService as debug } from "@core/services/debug.service";
import { AuthService } from '@core/services/auth/auth.service';
import { Objects } from '@carwashconnect/cwc-core-js';

export { AsyncStorage } from "@core/services/AsyncStorage";
export { UserReport, UserReportData } from "@core/data/userReport";
export { User } from "@core/data/user";

type UserReportMetaData = Omit<UserReportData, "Items">;

const USER_TYPES = {
    CUSTOMER: "customer",
    CLIENT: "client",
    MOSAIC: "cwc"
}

export { USER_TYPES };

@Injectable()
export class UserReportService {

    private _allUsers: AsyncStorage<UserReport>;
    private _targetClientId!: string;
    private _activeAllUsersClientId!: string;

    // user pagination
    private _page: number = 1;
    private _pageLength: number = 10;

    // user filters
    private _status: string[] = [];
    private _hasSubscriptions: boolean = false;
    private _filter: string = "";
    private _sortBy = "name";
    private _sortDirection = "asc";
    private _userRoles = [USER_TYPES.CUSTOMER, USER_TYPES.CLIENT];

    private _pseudoUsers: { [clientId: string]: UserReport[] } = {};

    private _metadata!: UserReportMetaData;

    constructor(
        private _http: HttpClient,
        private _auth: AuthService,
        private _clientsService: ClientsService,
        private _usersService: UserService
    ) {

        //Prepare user report storage
        this._allUsers = new AsyncStorage(this._http, { id: "UserReportAsyncStorage" });
        this._allUsers._setUpdateFunction(this._updateUsers());

        this._clientsService.clients.onActiveElementChange()
            .subscribe((client: Client) => {
                this._targetClientId = client.clientId;
                this._allUsers.update();
            });
    }

    public get users() { return this._allUsers };
    public get metadata(): UserReportMetaData { return this._metadata }
    public get usersClientId(): string { return this._activeAllUsersClientId };

    public addPseudoUser(user: UserReport): void {

        //Ensure there is an array for this client
        this._pseudoUsers[user.clientId] = this._pseudoUsers[user.clientId] || [];
        let foundUser: boolean = false;

        //Update user if the user is found
        for (let i in this._pseudoUsers[user.clientId]) {
            if (!this._userComparison(this._pseudoUsers[user.clientId][i], user)) continue;
            foundUser = true;
            this._pseudoUsers[user.clientId][i] = user;
            break;
        }

        //Add user if not found
        if (!foundUser)
            this._pseudoUsers[user.clientId].push({ ...user, isPseudoUser: true });

    }

    private _removePseudoUsers(users: UserReport[]): void {
        if (!users.length) return

        //Loop through provided users
        for (let user of users) {

            //If there's no list there's no pseudo users to remove
            if (!this._pseudoUsers[user.clientId] || !this._pseudoUsers[user.clientId].length) continue;

            //Remove the user if found
            for (let i = 0; i < this._pseudoUsers[user.clientId].length; i++) {
                if (!this._userComparison(this._pseudoUsers[user.clientId][i], user)) continue;
                this._pseudoUsers[user.clientId].splice(i, 1);
                break;
            }

        }

    }

    public async addUsers(emails: String[]): Promise<any> {

        //Add the users with the user service
        let addUsersResponse: APIResponse;
        try {

            addUsersResponse = await this._usersService.addUsers(emails);
            debug.log(addUsersResponse);

            if (addUsersResponse.data) {
                for (let i in addUsersResponse.data.users || {}) {
                    let user: User = addUsersResponse.data.users[i]
                    let reportUser: UserReport = {
                        clientId: user.clientId,
                        freeWashCount: 0,
                        email: user.email,
                        userId: user.userId,
                        type: user.type,
                        ssPurchaseCount: 0,
                        ssUsedCount: 0,
                        refundCount: 0,
                        status: "enable",
                        createdOn: (new Date()).getTime(),
                        isNewUser: user.newUser ?? false
                    };
                    this.addPseudoUser(reportUser)
                }
            }

        } catch (e) {
            debug.error(e);
            return Promise.reject(e)
        }

        //Update the users to trigger the notification
        this.users.update();

        return Promise.resolve(addUsersResponse.data);

    }

    private _userComparison(a: UserReport, b: UserReport): boolean {
        if (!a || !b) return false
        //Compare the two
        return a.userId == b.userId ? true : false;

    }

    public paginateUsers(page: number, pageLength: number, status: string[], hasSubscriptions: boolean, filter: string, sortBy: string, sortDirection: string, userRoles: string[]) {
        this._page = page || this._page;
        this._pageLength = pageLength || this._pageLength;
        this._status = (Array.isArray(this._status) ? status : this._status);
        this._hasSubscriptions = hasSubscriptions;
        this._filter = filter;
        this._sortBy = sortBy;
        this._sortDirection = sortDirection;
        this._userRoles = userRoles;


        if (!this._targetClientId) return;

        this._allUsers.update();
    }

    private _updateUsers(): (options?: any) => Observable<UserReport[]> {
        let self = this;
        return (lastEvaluatedKey?: any) => {

            //Prepare the inputs
            let inputs: { [key: string]: any } = {
                clientId: self._targetClientId,
                page: this._page,
                batchQuantity: this._pageLength,
                status: this._status,
                hasSubscriptions: this._hasSubscriptions,
                filter: this._filter,
                sortBy: this._sortBy,
                sortDirection: this._sortDirection,
                roles: this._userRoles
            }

            //Add the last key if present
            if ("undefined" != typeof lastEvaluatedKey) {
                inputs.lastEvaluatedKey = lastEvaluatedKey;
            }

            //return this.getUsers(inputs);
            return new Observable((observer) => {
                let promises: Promise<(UserReport[] | undefined)>[] = [];

                promises.push(this.getUsers(inputs));

                Promise.all(promises).then((data: (UserReport[] | undefined)[]) => {
                    return observer.next(data[0] as UserReport[]);
                });
            });
        }

    }

    public async getUserCsvExport(): Promise<UserReport[]> {
        //Prepare the inputs
        let inputs: { [key: string]: any } = {
            clientId: this._targetClientId,
            status: this._status,
            hasSubscriptions: this._hasSubscriptions,
            filter: this._filter,
            sortBy: this._sortBy,
            sortDirection: this._sortDirection,
            roles: this._userRoles
        }
        return await this.getUsers(inputs) as UserReport[];
    }

    private getUsers(inputData: { [key: string]: any }): Promise<UserReport[] | undefined> {
        const self: UserReportService = this;
        const input = Objects.copy(inputData);
        const endpoint = "reports/getCustomerReport";

        let userCount = 0;
        return self._http
            .post<APIResponse<UserReportData>>(endpoint, input)
            .pipe(
                // expand keeps calling until condition fails
                expand(res => {

                    if (res.errors?.length || !res.data)
                        throw new Error(res.errors[0])

                    //Copy the metadata for the latest request
                    const { Items, ...metadata } = res.data
                    this._metadata = metadata
                    userCount += metadata.Count;

                    //Check if there are more users to load
                    const alternateLastEvaluatedKey = !input.page && metadata.HasNextPage ? userCount : 0;
                    const lastEvaluatedKey = metadata.LastEvaluatedKey || alternateLastEvaluatedKey;
                    if (!lastEvaluatedKey)
                        return of()

                    input.lastEvaluatedKey = lastEvaluatedKey

                    return self._http.post<APIResponse<UserReportData>>(endpoint, input)

                }),
                map(response => {
                    return response.data?.Items ?? []
                }),
                reduce((all, current) => all.concat(current ?? []), [] as UserReport[]),
                map((response: UserReport[]) => {
                    //Filter out duplicate users
                    const uniqueUserIds: Set<string> = new Set([]);
                    const filteredUsers = response.concat(this._pseudoUsers[this._activeAllUsersClientId] ?? []).filter((user) => {
                        const id = `${user.clientId}:${user.userId}`;
                        if (uniqueUserIds.has(id)) {
                            return false
                        }
                        uniqueUserIds.add(id);
                        return true
                    })

                    //Update the client id value
                    this._activeAllUsersClientId = this._targetClientId;

                    return filteredUsers
                }),
            ).toPromise();

    }


    public async getDynamoUser(userId: string): Promise<User> {
        let user = null;

        try {
            const data: APIResponse<any> = <APIResponse<any>>(await this._http.post("users/getUsers", { clientId: this._targetClientId || this._auth.activeClient?.clientId, userId: userId }).toPromise());
            if (data["data"] && Array.isArray(data["data"]) && data["data"].length > 0) {
                user = data["data"][0];
            }
        } catch (ex) { }

        return user;
    }
}

export interface SaveUserInput {
    loyaltyPrograms: LoyaltyProgram[];
    addLoyalty: number;
    notes: string;
}

interface RootFilterOptions {
    onlyAdmin?: boolean;
    onlyCustomer?: boolean;
}
